import React, {useEffect, useState} from "react";
import {useDataWithRoute, useTitleFromComponent} from "../../../utils/hooks";
import {useNavigate} from "react-router-dom";
import {VsiBubble, VsiH4, VsiLgText, VsiMedText} from "../../../styled/App.styled";
import {Card, Row, UncontrolledTooltip} from "reactstrap";

import {useVsiAppContext} from "../../../stores/VsiApp";
import Grid from "../../../components/Grid";

import {EnvCard, EnvHeader} from "../../../styled/Environment";
import themeStyles from "../../../utils/themeStyles";
import {clusterUnitDefs} from "../../../utils/gridColumnDefs/virtual/allUnits";
import {
    determineEnvBackground,
    determineEnvBarColor,
} from "../../../utils/chartUtils";
import EnvironmentProgressBar from "../../../components/EnvironmentProgressBar";

const invalidCharsRegex = /[^a-zA-Z0-9\-_:.]/g;
    
const invalidIdRegex = /[.\/\s\\#]/g;

const bubbleStyle = (isTabletScreen) => ({
    display: 'grid',
    gridTemplateColumns: `repeat(${isTabletScreen ? 1 : 2}, 1fr)`,
    gridGap: '1rem 3rem',
    gridTemplateRows: 'masonry',
});

const VirtualEnvironmentClusterCard = ({clusterInfo, clusterName, group}) => {
    const { theme } = useVsiAppContext();
    let navigate = useNavigate();

    const {
        cpu_util_pct,
        memory_util_pct,
        disk_util_pct,
        total_memory,
        total_cpus,
        total_disk_allocated,
        total_vms,
        total_hosts,
    } = clusterInfo;

    const maxUtil = Math.max(cpu_util_pct, memory_util_pct, disk_util_pct);
    const bgColor = determineEnvBackground(maxUtil);
    const barColor = determineEnvBarColor(maxUtil);

    const utils = [
        {
            value: cpu_util_pct,
            name: 'CPU',
            metric: 'CPUs',
            capacity: parseFloat(total_cpus).toLocaleString(undefined, {minimumFractionDigits: 2}),
        },
        {
            value: memory_util_pct,
            name: 'Memory',
            metric: 'TiB',
            capacity: (Math.round(total_memory / 1024 * 10) / 10).toLocaleString(),
        },
        {
            value: disk_util_pct,
            name: 'Disk',
            metric: 'TiB',
            capacity: (Math.round(total_disk_allocated / 1024 * 10) / 10).toLocaleString(),
        }
    ];

    const onClusterClick = () => {
        navigate(`/reports/virtual/cluster/cluster-summary?cluster_name=${encodeURI(clusterName)}&vcenter_name=${encodeURI(group)}&period_id=${clusterInfo.period_id}`)
    }

    return <div className="p-2 d-flex flex-column"
                style={{
                    border: `1px solid ${barColor}`,
                    borderRadius: '5px',
                    backgroundColor: bgColor,
                    cursor: 'pointer',

                }}>
        <div className={"mb-3"}>
            <i className="fa-solid fa-network-wired me-2" style={{color: themeStyles[theme].textColor}}/>
            <VsiMedText textAlign={'left'} onDoubleClick={onClusterClick}>
                {clusterName}
            </VsiMedText>
        </div>
        <div className={"d-flex flex-row align-items-center justify-content-between"}>
            {utils.map((util, index) => {
                const {value, name, metric, capacity} = util;
                // const val = value > 100 ? 100 : value;
                return (
                    <div className={"w-100 mb-1 mt-1 ms-2 me-2 "} key={`${group}-${util.name}-${index}`}>
                        <div className={"d-flex flex-row align-items-center justify-content-between"}>
                            <VsiMedText textAlign={"left"}>% {name}</VsiMedText>
                        </div>
                        <EnvironmentProgressBar value={value}/>
                        <VsiMedText textAlign={"left"}>({capacity}) {metric}</VsiMedText>
                    </div>
                )
            })}
        </div>
        <div className={"p-2 d-flex flex-row align-items-center justify-content-around"}>
            {total_hosts ? <span>
                                  <i className="pe-1 fa-solid fa-globe"
                                     id={`virtual-card-${group.replace(invalidIdRegex, '')}-hosts-${clusterName.replace(invalidIdRegex, '').replace('(', '').replace(')', '')}-tip`}
                                     style={{color: themeStyles[theme].textColor}}/>
                                  <UncontrolledTooltip
                                      target={`virtual-card-${group.replace(invalidIdRegex, '')}-hosts-${clusterName.replace(invalidIdRegex, '').replace('(', '').replace(')', '')}-tip`}>
                                      Hosts
                                  </UncontrolledTooltip>
                                  <VsiLgText>{total_hosts}</VsiLgText>
                              </span> : null}
            {total_vms ? <span>
                                      <i className="pe-1 fa-solid fa-boxes-stacked"
                                         id={`virtual-card-${group.replace(invalidIdRegex, '')}-vms-${clusterName.replace(invalidIdRegex, '').replace('(', '').replace(')', '')}-tip`}
                                         style={{color: themeStyles[theme].textColor}}/>
                                <UncontrolledTooltip
                                    target={`virtual-card-${group.replace(invalidIdRegex, '')}-vms-${clusterName.replace(invalidIdRegex, '').replace('(', '').replace(')', '')}-tip`}>
                                      VMs
                                  </UncontrolledTooltip>
                                  <VsiLgText>
                                      {total_vms}
                                  </VsiLgText>
                              </span> : null}
        </div>
    </div>
}

const VirtualEnvironmentCard = ({keyData, name}) => {
    const { theme, isTabletScreen } = useVsiAppContext();

    const {
        total_virtual_cpus_used,
        total_virtual_cpus,
        total_memory,
        total_memory_used,
        total_disk_used,
        total_disk_allocated,
        avg_ghz_per_cpu,
    } = keyData;

    const cpuUtil = Math.round((total_virtual_cpus_used / total_virtual_cpus) * 100);
    const memUtil = Math.round((total_memory_used / total_memory) * 100);
    const diskUtil = Math.round((total_disk_used / total_disk_allocated) * 100);

    const background = determineEnvBackground(Math.max(...[cpuUtil, memUtil, diskUtil]));
    const utils = [
        {
            value: cpuUtil,
            name: 'CPU',
            metric: 'GHZ',
            capacity: Math.round((total_virtual_cpus * avg_ghz_per_cpu)).toLocaleString(),
        },
        {
            value: memUtil,
            name: 'Memory',
            metric: 'TiB',
            capacity: (Math.round(total_memory / 1024 * 10) / 10).toLocaleString(),
        },
        {
            value: diskUtil,
            name: 'Disk',
            metric: 'TiB',
            capacity: (Math.round(total_disk_allocated / 1024 * 10) / 10).toLocaleString(),
        }
    ];
    const allClusters = Object.keys(keyData.all_clusters);

    return (
        <EnvCard>
            <EnvHeader backgroundColor={background}>
                <div className={"d-flex flex-row align-items-center justify-content-between mb-2"}>
                    <div>
                        <i className={`fa-solid fa-circle-nodes me-2`} style={{color: themeStyles[theme].textColor}}/>
                        <VsiLgText>
                            {name}
                        </VsiLgText>
                    </div>
                    <div>
                        <VsiLgText>{Math.round(total_memory * 10 / 1024) / 10?.toLocaleString?.() ?? 0} Memory
                            TiB</VsiLgText>
                    </div>
                </div>
                <div className={"d-flex flex-row align-items-center justify-content-between"}>
                    {utils.map((util, index) => {
                        const {value, name, metric, capacity} = util;

                        return (
                            <div className={"w-100 mb-1 mt-1 ms-2 me-2 "} key={`${name}-${util.name}-${index}`}>
                                <div className={"d-flex flex-row align-items-center justify-content-between"}>
                                    <VsiMedText textAlign={"left"}>% {name}</VsiMedText>
                                    <VsiMedText textAlign={"left"}>({capacity}) {metric}</VsiMedText>
                                </div>
                                <EnvironmentProgressBar value={value}/>
                            </div>
                        )
                    })}
                </div>
            </EnvHeader>
            <VsiMedText className={"p-2"}>
                Clusters ({allClusters.length}):
            </VsiMedText>
            <div className={"p-2"} style={bubbleStyle(isTabletScreen)}>
                {allClusters.map((typeKey, typeIndex) => {
                    const clusterInfo = keyData.all_clusters[[typeKey]];
                    return <VirtualEnvironmentClusterCard key={`cluster-${typeKey}-${typeIndex}`}
                                                          group={name}
                                                          clusterName={typeKey}
                                                          clusterInfo={clusterInfo}/>
                })}
            </div>
        </EnvCard>
    )
}

export default function VirtualEnvironment({context, title}) {
    useTitleFromComponent(`${context} / ${title}`);
    const { theme, isTabletScreen, client, periodId } = useVsiAppContext();

    const [visibleData, setVisibleData] = useState([]);
    const [envData, setEnvData] = useState([]);

    const [{recordset: data}] = useDataWithRoute(`/api/v6/report/enterprise_virtual_all_clusters_proc?client_id=${client.clientId}&period_id=${periodId}`);

    const createEnvironmentGrouping = data => {
        const grouping = {};
        data.forEach(datum => {
            const total_memory_used = datum.total_memory * (datum.memory_util_pct / 100);
            if (!grouping[datum["vcenter_name"]]) {
                grouping[datum["vcenter_name"]] = {
                    ...datum,
                    total_memory_used,
                    all_clusters: {
                        [datum["cluster_name"].trim()]: {
                            ...datum,
                            total_memory_used,
                        }
                    }
                };
            } else {
                Object.keys(datum).forEach(key => {
                    if (typeof datum[key] === "number" && !key.includes("_id")) {
                        const num = grouping[datum["vcenter_name"]][key];
                        grouping[datum["vcenter_name"]][key] = num + datum[key];
                        if (!grouping[datum["vcenter_name"]].all_clusters[datum["cluster_name"].trim()]) {
                            grouping[datum["vcenter_name"]].all_clusters[datum["cluster_name"].trim()] = {...datum}
                        }
                    }
                });
            }
        });
        return grouping;
    }

    useEffect(() => {
        setVisibleData(data);
    }, [data]);

    useEffect(() => {
        const grouping = createEnvironmentGrouping(visibleData);
        setEnvData(grouping);
    }, [visibleData]);

    const filterChanged = (eventData) => {
        let rowData = [];
        eventData.api.forEachNodeAfterFilter(node => {
            rowData.push(node.data);
        });
        setVisibleData(rowData || []);
    }

    const envKeys = Object.keys(envData);

    const allClusterLengths = envKeys.map(key => ({
        vCenter: key,
        clusterCount: Object.keys(envData[key].all_clusters).length
    })).sort((a, b) => b.clusterCount - a.clusterCount);

    const sides = function () {
        const left = {
            centers: {},
            count: 0,
        };
        const right = {
            centers: {},
            count: 0,
        };

        allClusterLengths.forEach((center) => {
            if (left.count > (right.count + center.clusterCount)) {
                right.count = right.count + center.clusterCount;
                right.centers[center.vCenter] = envData[center.vCenter];
            } else {
                left.count = left.count + center.clusterCount;
                left.centers[center.vCenter] = envData[center.vCenter];
            }
        });

        return {
            left,
            right,
        }
    }();

    const leftSide = Object.keys(sides.left.centers);
    const rightSide = Object.keys(sides.right.centers);

    return (
        <Card className={`report-card p-2 nav-card-${theme}`}>
            <VsiH4>Virtual Environment</VsiH4>
            <VsiBubble autobotName='VirtualEnvironmentCards' autobotInfo={{data: envData}}>
            {/* <VsiBubble noAutobot> */}
                {isTabletScreen ? (
                    <div className={"w-100 px-2"}>
                        {envKeys.map((key, index) => {
                            const keyData = envData[key];
                            return (
                                <VirtualEnvironmentCard key={`left-column-${key}-${index}`}
                                                        keyData={keyData}
                                                        name={key}/>
                            )
                        })}
                    </div>
                ) : (
                    <Row>
                        <div className={"w-50 px-2"}>
                            {leftSide.map((key, index) => {
                                const keyData = envData[key];
                                return (
                                    <VirtualEnvironmentCard key={`left-column-${key}-${index}`}
                                                            keyData={keyData}
                                                            name={key}/>
                                )
                            })}
                        </div>
                        <div className={"w-50 px-2"}>
                            {rightSide.map((key, index) => {
                                const keyData = envData[key];
                                return (
                                    <VirtualEnvironmentCard key={`right-column-${key}-${index}`}
                                                            keyData={keyData}
                                                            name={key}/>
                                )
                            })}
                        </div>
                    </Row>
                )}
            </VsiBubble>
            <VsiBubble>
                <Grid data={data}
                      columnDefs={clusterUnitDefs}
                      filterChanged={filterChanged}/>
            </VsiBubble>
        </Card>
    )
}
